import { usePage } from "@inertiajs/react";
import { LoaderCircle } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { CompleteAuthentication } from "@/components/bank-id/components/complete-authentication";
import { FailedAuthentication } from "@/components/bank-id/components/failed-authentication";
import { PendingAuthentication } from "@/components/bank-id/components/pending-authentication";
import { BankIdButton } from "@/components/bank-id/components/utilities/bank-id-button";
import { BankIdStatus } from "@/components/bank-id/types";
import { BankIdLogo } from "@/components/bank-id/utilities/bank-id-logo";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useTranslations } from "@/hooks/use-translations";
import SwitchAuthProcessLink from "@/pages/auth/switch-auth-process-link";
import {
  useBankIdActions,
  useBankIdStore,
} from "@/stores/bank-id/bank-id-store";
import type {
  BankIdState,
  BankIdStore,
} from "@/stores/bank-id/bank-id-store.types";
import { AuthProcess, type SharedData } from "@/types";

type BankIdProps = {
  authProcess: AuthProcess;
  intendedUrl: string;
};

export function BankId({ authProcess, intendedUrl }: BankIdProps) {
  const { __ } = useTranslations();
  const { config } = usePage<SharedData>().props;

  const {
    handleCreateAuthenticationOrder,
    handleActivateAuthenticationOrder,
    handleCancelAuthenticationOrderManually,
  } = useBankIdActions();

  const [showAuthenticationDialog, setShowAuthenticationDialog] =
    useState(false);

  const isRegisterFlow = authProcess === AuthProcess.Register;

  const { sessionCreated, status, isLoading, acceptedTerms, showTermsWarning } =
    useBankIdStore((state: BankIdState) => state);
  const {
    setIntendedUrl,
    setIsLoading,
    setAcceptedTerms,
    setShowTermsWarning,
  } = useBankIdStore((state: BankIdStore) => state);

  const dialogChangedOpenStatus = async (show: boolean) => {
    setShowAuthenticationDialog(show);

    if (!show && status !== BankIdStatus.Failed) {
      // Cancel auth order when the modal is closed
      await handleCancelAuthenticationOrderManually();
    }
  };

  const closeModal = async () => {
    await dialogChangedOpenStatus(false);
  };

  const activateAuthenticationOrder = async () => {
    if (isRegisterFlow && !acceptedTerms) {
      setShowTermsWarning(true);

      return;
    }

    await handleActivateAuthenticationOrder();

    setIntendedUrl(intendedUrl);

    setShowAuthenticationDialog(true);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Would cause endless loop
  useEffect(() => {
    setIsLoading(true);

    const { hash } = window.location;

    let sessionId: string | null = null;

    if (hash?.startsWith("#nonce=")) {
      sessionId = hash.replace("#nonce=", "");
    }

    handleCreateAuthenticationOrder(authProcess, sessionId);
  }, []);

  return (
    <>
      <div className={"mb-6 flex flex-col gap-4"}>
        <BankIdButton
          className={"mx-auto w-full min-w-[300px]"}
          disabled={!sessionCreated}
          onClick={activateAuthenticationOrder}
        >
          <BankIdLogo className={"mr-1 h-6 w-6"} inverted={false} />
          {isLoading && <LoaderCircle className="h-4 w-4 animate-spin" />}
          {!isLoading &&
            authProcess === AuthProcess.LogIn &&
            __("bank_id.identify_with_bank_id")}
          {!isLoading &&
            authProcess === AuthProcess.Register &&
            __("bank_id.register_with_bank_id")}
        </BankIdButton>

        {isRegisterFlow && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={acceptedTerms}
                id="accepted_terms"
                onCheckedChange={(checked) => {
                  setAcceptedTerms(checked === true);
                  setShowTermsWarning(false);
                }}
              />
              <Label
                className="font-normal text-sm leading-snug"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for inserting HTML links into translated text
                dangerouslySetInnerHTML={{
                  __html: __("auth.register_page.accept_terms", {
                    terms_link: `<a class="underline hover:no-underline" href="${config.termsOfServiceUrl ?? "#"}" rel="noopener noreferrer" target="_blank">${__("auth.register_page.terms_of_service")}</a>`,
                    privacy_link: `<a class="underline hover:no-underline" href="${config.privacyPolicyUrl ?? "#"}" rel="noopener noreferrer" target="_blank">${__("auth.register_page.privacy_policy")}</a>`,
                  }),
                }}
                htmlFor="accepted_terms"
              />
            </div>

            <AnimatePresence>
              {showTermsWarning && (
                <motion.p
                  animate={{ opacity: 1, height: "auto" }}
                  className="text-destructive-foreground text-sm"
                  exit={{ opacity: 0, height: 0 }}
                  initial={{ opacity: 0, height: 0 }}
                >
                  {__("auth.register_page.accept_terms_warning")}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      <Dialog
        onOpenChange={dialogChangedOpenStatus}
        open={showAuthenticationDialog}
      >
        {status === BankIdStatus.Pending && (
          <PendingAuthentication
            authProcess={authProcess}
            closeModal={closeModal}
          />
        )}
        {status === BankIdStatus.Failed && (
          <FailedAuthentication
            authProcess={authProcess}
            closeModal={closeModal}
          />
        )}
        {status === BankIdStatus.Complete && <CompleteAuthentication />}
      </Dialog>

      <SwitchAuthProcessLink authProcess={authProcess} />
    </>
  );
}
